"use client"

import { useState, type ReactNode } from "react"

import { Clapperboard, Download, Paperclip } from "lucide-react"

import type { MediaKind, ThreadMedia } from "@/lib/conversation/message-media"
import { cn } from "@/lib/utils"
import { useTranslations } from "next-intl"


/**
 * The desktop size. It sits on a wrapper, fixed, while the element itself only gets
 * `max-w-full`: a percentage cap is ignored when the bubble sizes itself to its
 * content, so on the element alone a 1080px photo would stretch the bubble to its
 * limit. A phone leaves the bubble well under 240px, and the wrapper shrinks with it.
 */
const MEDIA_MAX_WIDTH = "max-w-[240px]"

/** A messaging attachment, drawn the same way in both inboxes. */
export function ConversationMedia({ media }: { media: ThreadMedia }) {
  switch (media.kind) {
    case "file":
      return <FileNameContent media={media} />
    case "audio":
      return <AudioContent media={media} />
    case "image":
    case "video":
    case "unknown":
      return (
        <span className={cn("inline-block", MEDIA_MAX_WIDTH)}>
          <MediaCaption media={media} />
          <MediaWithDownload media={media}>
            <DrawableMedia kind={media.kind} url={media.url} />
          </MediaWithDownload>
        </span>
      )
  }
}

/**
 * Paperclip + filename — the normal rendering for a file and the degraded one
 * for media whose private copy has no usable URL (over the 20 MB cap, failed
 * download, failed signing). Link when a URL exists, plain name otherwise.
 */
function FileNameContent({ media }: { media: ThreadMedia }) {
  const href = downloadHref(media)
  const fileName = media.fileName ?? "file"

  return (
    <span className="inline-flex max-w-full items-center gap-1.5 text-sm">
      <Paperclip className="h-3.5 w-3.5 shrink-0" />
      {href ? (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="min-w-0 underline underline-offset-2 break-all"
        >
          {fileName}
        </a>
      ) : (
        <span className="min-w-0 break-all">{fileName}</span>
      )}
    </span>
  )
}

/** Platform-CDN media has no download URL — falls back to opening in a tab. */
function downloadHref(media: ThreadMedia): string | null {
  return media.downloadUrl ?? media.url
}

function AudioContent({ media }: { media: Extract<ThreadMedia, { url: string }> }) {
  const tUi = useTranslations("Feedback.ConversationMedia")

  const { src, onError } = useSteadySrc(media.url)
  const href = downloadHref(media)

  return (
    <span className="inline-flex max-w-full items-center gap-1.5">
      <audio src={src} onError={onError} controls className={cn("min-w-0", MEDIA_MAX_WIDTH)} preload="metadata" />
      {href && (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={tUi("downloadAudio")}
          title={tUi("download")}
          className="shrink-0 rounded-md p-1 text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <Download className="h-3.5 w-3.5" />
        </a>
      )}
    </span>
  )
}

function MediaCaption({ media }: { media: ThreadMedia }) {
  const tUi = useTranslations("Feedback.ConversationMedia")
  if (!media.storyReply) return null

  return (
    <span className="mb-1 flex items-center gap-1 text-[11px] opacity-70">
      <Clapperboard className="h-3 w-3 shrink-0" />
      <span key={tUi("storyReply")}>{tUi("storyReply")}</span>
    </span>
  )
}

function MediaWithDownload({ media, children }: { media: ThreadMedia; children: ReactNode }) {
  const tUi = useTranslations("Feedback.ConversationMedia")

  const href = downloadHref(media)
  if (!href) return <>{children}</>

  return (
    <span className="group/media relative inline-block max-w-full">
      {children}
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={tUi("downloadAttachment")}
        title={tUi("download")}
        className="absolute top-1.5 right-1.5 rounded-md bg-black/60 p-1.5 text-white opacity-0 transition-opacity hover:bg-black/80 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white group-hover/media:opacity-100"
      >
        <Download className="h-3.5 w-3.5" />
      </a>
    </span>
  )
}

/** Only the kinds drawn inline — `audio` and `file` are answered by the caller. */
type DrawableKind = Extract<MediaKind, "image" | "video" | "unknown">

function DrawableMedia({ kind, url }: { kind: DrawableKind; url: string }) {
  const { src, onError } = useSteadySrc(url)

  switch (kind) {
    case "video":
      return <video src={src} onError={onError} controls className="max-w-full rounded-lg" preload="metadata" />
    case "image":
      // eslint-disable-next-line @next/next/no-img-element -- external CDN media (Instagram/Facebook), domains not predictable
      return <img src={src} onError={onError} alt="" className="h-auto max-w-full rounded-lg" loading="lazy" />
    case "unknown":
      return <UntypedMedia src={src} onVideoError={onError} />
  }
}

/**
 * Private media is signed afresh whenever a thread is read, and the unified inbox
 * re-reads its thread every few seconds: a new token in `src` would rewind audio and
 * video and download images again on every poll. Keeps the element's `src` while it
 * points at the same object — links are left alone, a new `href` reloads nothing —
 * and takes the newest signature once a load fails, which is what an expired one does.
 */
function useSteadySrc(url: string): { src: string; onError: () => void } {
  const [shown, setShown] = useState(url)
  const [failed, setFailed] = useState(false)

  if (withoutQuery(shown) !== withoutQuery(url) || (failed && shown !== url)) {
    setShown(url)
    setFailed(false)
  }
  return { src: shown, onError: () => setFailed(true) }
}

/** A signed URL differs per signing only in its query string. */
function withoutQuery(url: string): string {
  return url.split("?")[0]
}

/**
 * Only for rows whose kind we never learned (media ingested before the channel stored a
 * `mime_type`). Probe with the image and swap on decode failure — a video drawn into `<img>`
 * is the blank bubble this replaces.
 */
function UntypedMedia({ src, onVideoError }: { src: string; onVideoError: () => void }) {
  const [isVideo, setIsVideo] = useState(false)

  if (isVideo) {
    return <video src={src} onError={onVideoError} controls className="w-[220px] max-w-full rounded-lg" preload="metadata" />
  }

  return (
    // eslint-disable-next-line @next/next/no-img-element -- external CDN media (Instagram/Facebook), domains not predictable
    <img
      src={src}
      alt=""
      className="w-[220px] max-w-full rounded-lg bg-black/10 dark:bg-white/10"
      loading="lazy"
      onError={() => setIsVideo(true)}
    />
  )
}
